import { BudgetSheetPayload, BudgetSheetPerson, MemberCountry } from '@data/utils/types';

import { SENIORITY_LEVELS } from '../../members/memberMock';
import { type BudgetTeamMember, type VersionTeam, teamAt } from './budget';
import { costMatrixRate } from './costs';
import { type FractionalPerson, fractionalRate } from './fractional';
import { QUARTERS, QuarterLine } from './quarterCosts';
import { AGREEMENT_TERMS, FLAT_FEE, PRIORITIES, type SoldSupport } from './serviceLevels';

/** A sprint as the grid draws it, which is all the workbook needs to head a column. */
export interface SheetSprint {
  id: string;
  number: number;
  startDate: string;
}

export interface SheetGroup {
  label: string;
  members: BudgetTeamMember[];
}

interface Source {
  year: number;
  productName: string;
  sprints: SheetSprint[];
  closedSprintIds: string[];
  groups: SheetGroup[];
  fractional: FractionalPerson[];
  /** Hours by sprint id, then by row id: the members' and the fractional rows' together. */
  hours: Record<string, Record<string, number>>;
  teams: VersionTeam[];
  costs: Record<string, QuarterLine[]>;
  service: SoldSupport | null;
  sectionLabel: (section: string) => string;
}

/** A person's hours across the year, in the order the columns run. Nothing budgeted reads as zero. */
const hoursAcross = (sprints: SheetSprint[], hours: Source['hours'], personId: string): number[] =>
  sprints.map(sprint => hours[sprint.id]?.[personId] ?? 0);

/** What a member's hour costs in one sprint, under the version that sprint falls in. */
const rateIn = (teams: VersionTeam[], startDate: string, member: BudgetTeamMember): number | null => {
  const terms = teamAt(teams, startDate);
  if (!terms) return null;
  const matrix = terms.matrices[member.country] ?? terms.matrices[MemberCountry.belgium] ?? {};
  return costMatrixRate(matrix, member.chapterKey, member.level);
};

/** One per sprint, so a member repriced mid-year is billed at each sprint's own rate. */
const ratesAcross = (teams: VersionTeam[], sprints: SheetSprint[], member: BudgetTeamMember): (number | null)[] =>
  sprints.map(sprint => rateIn(teams, sprint.startDate, member));

/** The rate column holds one figure: the latest agreed, which is what the person costs now. */
const latestRate = (rates: (number | null)[]): number | null =>
  [...rates].reverse().find(rate => rate !== null) ?? null;

const memberRow = (member: BudgetTeamMember, source: Source): BudgetSheetPerson => {
  const rates = ratesAcross(source.teams, source.sprints, member);
  return {
    name: member.name,
    country: member.country,
    level: member.level,
    rate: latestRate(rates),
    // Nothing agreed is sent as nothing, which is what the sheet's own empty rate cell prices at.
    rates: rates.map(rate => rate ?? 0),
    hours: hoursAcross(source.sprints, source.hours, member.id),
  };
};

/** A fractional row carries its own rate, agreed for the year, and has no chapter or level. */
const fractionalRow = (person: FractionalPerson, source: Source): BudgetSheetPerson => {
  const rate = fractionalRate(source.fractional, person.id);
  return {
    name: person.name,
    rate,
    rates: source.sprints.map(() => rate ?? 0),
    hours: hoursAcross(source.sprints, source.hours, person.id),
  };
};

/** A group's chapter key, which is what the matrix is keyed by. Taken off its people, who all share it. */
const chapterOf = (group: SheetGroup): string => group.members[0]?.chapterKey ?? '';

/** The latest terms the year runs under, for the same reason a member's rate column is. */
const rateCard = (source: Source): BudgetSheetPayload['rateCard'] => {
  const last = source.sprints[source.sprints.length - 1];
  const opening = last ? teamAt(source.teams, last.startDate) : null;
  if (!opening) return [];

  return source.groups.flatMap(group =>
    SENIORITY_LEVELS.map(level => ({
      chapter: group.label,
      level,
      belgium: costMatrixRate(opening.matrices[MemberCountry.belgium] ?? {}, chapterOf(group), level),
      ukraine: costMatrixRate(opening.matrices[MemberCountry.ukraine] ?? {}, chapterOf(group), level),
    })),
  );
};

/** The quarters of one out of pocket line, always four, in order. A quarter nobody filled in is zero. */
const quartersOf = (line: QuarterLine): number[] => QUARTERS.map(quarter => line.amounts[quarter] ?? 0);

/** The agreement's window, plus the targets as negotiated on this product. A flat fee has neither. */
const serviceOf = (service: SoldSupport | null): BudgetSheetPayload['service'] => {
  if (!service || service.agreement === FLAT_FEE) return undefined;
  const terms = AGREEMENT_TERMS[service.agreement];
  return {
    from: terms.from,
    to: terms.to,
    days: terms.days,
    targets: PRIORITIES.map(priority => ({
      priority,
      response: service.targets[priority].response,
      resolution: service.targets[priority].resolution,
    })),
  };
};

/** From what the grid was drawn from, not the store, so the sheet and the screen cannot disagree. */
export const budgetSheetPayload = (source: Source): BudgetSheetPayload => ({
  year: source.year,
  productName: source.productName,
  sprints: source.sprints.map(sprint => ({
    number: sprint.number,
    startDate: sprint.startDate,
    isClosed: source.closedSprintIds.includes(sprint.id),
  })),
  groups: source.groups.map(group => ({
    label: group.label,
    people: group.members.map(member => memberRow(member, source)),
  })),
  fractional: source.fractional.map(person => fractionalRow(person, source)),
  outOfPocket: Object.entries(source.costs).flatMap(([section, lines]) =>
    lines
      .filter(line => line.name)
      .map(line => ({ section: source.sectionLabel(section), name: line.name, quarters: quartersOf(line) })),
  ),
  rateCard: rateCard(source),
  service: serviceOf(source.service),
});
